import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API_URL from '../../config/api';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700;800&display=swap');

.tl-wrap {
  min-height: 100vh;
  background: linear-gradient(160deg, #064e3b 0%, #0d9488 40%, #0369a1 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  padding: 24px 16px;
  position: relative;
  overflow: hidden;
}
.tl-wrap::before {
  content: '';
  position: absolute;
  top: -20%; right: -10%;
  width: 500px; height: 500px;
  background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
  border-radius: 50%;
}
.tl-wrap::after {
  content: '';
  position: absolute;
  bottom: -15%; left: -8%;
  width: 400px; height: 400px;
  background: radial-gradient(circle, rgba(3,105,161,0.25) 0%, transparent 70%);
  border-radius: 50%;
}
.tl-card {
  background: #fff;
  border-radius: 24px;
  padding: 44px 40px;
  max-width: 420px;
  width: 100%;
  box-shadow:
    0 20px 0 rgba(6,78,59,0.2),
    0 32px 64px rgba(0,0,0,0.22),
    inset 0 1px 0 rgba(255,255,255,0.9);
  position: relative;
  z-index: 1;
}
.tl-icon-wrap {
  width: 68px; height: 68px;
  background: linear-gradient(135deg, #064e3b, #0d9488, #0369a1);
  border-radius: 18px;
  display: flex; align-items: center; justify-content: center;
  font-size: 32px;
  margin: 0 auto 16px;
  box-shadow: 0 8px 24px rgba(13,148,136,0.35);
}
.tl-title {
  margin: 0 0 4px;
  font-size: 24px; font-weight: 800; color: #0f172a;
  text-align: center; letter-spacing: -0.02em;
}
.tl-subtitle {
  margin: 0 0 32px;
  font-size: 14px; color: #64748b;
  text-align: center; font-weight: 500;
}
.tl-label {
  display: block; font-size: 13px; font-weight: 700;
  color: #374151; margin-bottom: 6px;
}
.tl-input {
  width: 100%; padding: 12px 14px;
  border: 1.5px solid #d1d5db; border-radius: 10px;
  font-size: 15px; outline: none;
  font-family: inherit; box-sizing: border-box;
  transition: border-color 0.15s;
  color: #0f172a;
}
.tl-input:focus { border-color: #0d9488; box-shadow: 0 0 0 3px rgba(13,148,136,0.1); }
.tl-btn {
  width: 100%;
  background: linear-gradient(135deg, #0d9488, #0891b2);
  color: #fff; border: none; border-radius: 11px;
  padding: 14px; font-size: 15px; font-weight: 800;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 4px 0 rgba(6,78,59,0.3), 0 8px 20px rgba(13,148,136,0.35);
  transition: opacity 0.15s, transform 0.12s;
  letter-spacing: -0.01em;
}
.tl-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
.tl-btn:active:not(:disabled) { transform: translateY(1px); box-shadow: 0 2px 0 rgba(6,78,59,0.3); }
.tl-btn:disabled { background: #9ca3af; cursor: not-allowed; box-shadow: none; }
.tl-error {
  background: #fef2f2; border: 1.5px solid #fca5a5;
  border-radius: 9px; padding: 10px 14px;
  margin-bottom: 20px; font-size: 13px; color: #dc2626;
  display: flex; align-items: center; gap: 8px;
}
.tl-hint {
  text-align: center; margin-top: 22px;
  font-size: 13px; color: #64748b; line-height: 1.5;
}
.tl-footer {
  position: relative; z-index: 1;
  margin-top: 20px; font-size: 12px;
  color: rgba(255,255,255,0.7); text-align: center;
}
.tl-footer a { color: #a7f3d0; text-decoration: none; font-weight: 600; }
.tl-footer a:hover { color: #fff; }
`;

const TenantLogin: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/tenant-portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) {
        let msg = data.error || 'Login failed';
        if (data.code === 'INVITE_PENDING') {
          msg += ' If you cannot find the email, ask your landlord to resend the portal invite.';
        } else if (data.code === 'INVITE_EXPIRED') {
          msg += ' Your landlord can send a fresh invite from the Tenants page.';
        } else if (data.code === 'NOT_INVITED') {
          msg += ' You cannot sign in until your landlord invites you.';
        }
        setError(msg);
        return;
      }
      localStorage.setItem('tenantToken', data.token);
      localStorage.setItem('tenantInfo', JSON.stringify(data.tenant));
      navigate('/tenant/portal');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tl-wrap">
      <style>{CSS}</style>

      <div className="tl-card">
        <div className="tl-icon-wrap">🏠</div>
        <h1 className="tl-title">Tenant Portal</h1>
        <p className="tl-subtitle">Sign in to manage your tenancy</p>

        {error && (
          <div className="tl-error">
            <span>⚠️</span> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 18 }}>
            <label className="tl-label">Email address</label>
            <input
              className="tl-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
              autoComplete="email"
            />
          </div>
          <div style={{ marginBottom: 26 }}>
            <label className="tl-label">Password</label>
            <input
              className="tl-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              autoComplete="current-password"
            />
            <div style={{ textAlign: 'right', marginTop: 6 }}>
              <Link to="/tenant/forgot-password" style={{ fontSize: 13, color: '#0d9488', fontWeight: 600, textDecoration: 'none' }}>
                Forgot password?
              </Link>
            </div>
          </div>
          <button type="submit" className="tl-btn" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In →'}
          </button>
        </form>

        <p className="tl-hint">
          No account yet?<br />
          <span style={{ color: '#0d9488', fontWeight: 600 }}>Use the invitation link from your landlord — sign in is only at app.shahosuite.com/tenant/login</span>
        </p>
      </div>

      <p className="tl-footer">
        Are you a landlord?{' '}
        <Link to="/login">Sign in here</Link>
      </p>
    </div>
  );
};

export default TenantLogin;
